import {
	ArrowBack,
	Close,
	DeleteOutlined,
	EditOutlined,
	InfoOutlined,
	LabelOutlined,
	MoreHoriz,
	PeopleAltOutlined,
	RestoreFromTrashOutlined,
} from '@mui/icons-material';
import {
	Box,
	Button,
	Chip,
	Dialog,
	Divider,
	IconButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { EmblemAvatar } from 'components/ui/emblem/emblem-avatar';
import { EmblemAvatarGroup } from 'components/ui/emblem/emblem-avatar-group';
import { ModalOrDrawer } from 'components/ui/modals/modal-or-drawer';
import {
	Conversation,
	ConversationParticipant,
	LabelType,
	ParticipantEntityType,
} from 'middleware-types';
import { useRef, useState } from 'react';
import { useIsMobile } from 'utils/useScreenSize';
import { useCommunicationsContext } from '../communications-provider';
import { getCustomLabels } from '../helpers/labels';
import { convertParticipantType } from '../helpers/utils';
import { useRestoreConversation } from '../hooks/conversation-labels-hooks';
import { useConversation } from '../hooks/conversations-hooks';
import { AddParticipantsModal } from '../modals/add-participants-modal';
import { LabelsPopup } from '../modals/labels-popup';
import { ParticipantsPopup } from '../modals/participants-popup';
import { RemoveParticipantModal } from '../modals/remove-participant-modal';
import { TrashConversationModal } from '../modals/trash-conversation-modal';
import { UpdateConversationModal } from '../modals/update-conversation-modal';
import { PopoverOrDrawer } from '../shared/popover-or-drawer';
import { ConversationColumnSkeleton } from './conversation-column-skeleton';
import { ConversationMessageBox } from './conversation-message-box';
import { EmptyConversationColumn } from './empty-conversation-column';
import { LabelChip } from './label-chip';
import { MessagesList } from './messages-list';

interface ConversationColumnProps {
	conversation: Conversation;
}

const _ConversationColumn = ({ conversation }: ConversationColumnProps) => {
	const isMobile = useIsMobile();
	const {
		entityId,
		labels: _labels,
		setSelectedConversationId,
		readOnly,
	} = useCommunicationsContext();

	const restoreConversation = useRestoreConversation();

	// menu states
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

	const labelsPopupAnchor = useRef<HTMLButtonElement | null>(null);
	const [labelsPopupOpen, setLabelsPopupOpen] = useState(false);

	const participantsPopupAnchor = useRef<HTMLDivElement | null>(null);
	const [participantsPopupOpen, setParticipantsPopupOpen] = useState(false);

	const [replyModalOpen, setReplyModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [trashModalOpen, setTrashModalOpen] = useState(false);
	const [addParticipantsModalOpen, setAddParticipantsModalOpen] = useState(false);
	const [removeParticipantModalOpen, setRemoveParticipantModalOpen] = useState(false);
	const [participantToRemove, setParticipantToRemove] = useState<ConversationParticipant>();

	// remove inactive participants, and put the current entity last in the list
	const participants = conversation.participants
		.filter((p) => p.isCurrentParticipant && p.entityType !== ParticipantEntityType.System)
		.sort((a, b) => {
			const aValue = a.entityId === entityId ? 1 : 0;
			const bValue = b.entityId === entityId ? 1 : 0;
			return aValue - bValue;
		});

	// determine if the current entity is an active participant
	const isActiveParticipant = participants.find((p) => p.entityId === entityId) !== undefined;
	const canEditConversation = isActiveParticipant && !readOnly;

	// determine if this is a private conversation
	const isPrivate = participants.length > 0 && participants.every((p) => p.entityId === entityId);

	// get labels
	const labels = getCustomLabels(_labels, true);
	const participantLabels =
		conversation.participants.find((p) => p.entityId === entityId)?.participantLabels ?? [];
	const activeLabelIds = participantLabels.map((l) => l.id);
	const activeLabels = labels.filter((label) => activeLabelIds.includes(label.label.id));

	const isInTrash = participantLabels.some((label) => label.type === LabelType.Trash);

	return (
		<>
			<Stack height="100%">
				<Paper elevation={1} sx={{ borderRadius: 0, zIndex: 100 }}>
					<Stack
						direction="row"
						px={2}
						py={{ xs: 1, sm: 1.5 }}
						pl={{ xs: 1, sm: 2 }}
						alignItems="center"
						spacing={0.5}>
						{isMobile && (
							<IconButton onClick={() => setSelectedConversationId(null)}>
								<ArrowBack />
							</IconButton>
						)}
						<Typography sx={{ wordBreak: 'break-word' }} variant="h4">
							{conversation.subject}
						</Typography>
						{isPrivate && (
							<Tooltip title="You are the only participant in this conversation.">
								<Chip
									size="small"
									label={
										<Stack direction="row" alignItems="center" spacing={0.5}>
											<Typography variant="inherit" color="inherit">
												Private
											</Typography>
											<InfoOutlined
												sx={{ fontSize: 16, color: 'neutral.500' }}
											/>
										</Stack>
									}
								/>
							</Tooltip>
						)}
						{!readOnly && (
							<IconButton
								ref={labelsPopupAnchor}
								onClick={() => setLabelsPopupOpen(true)}>
								<LabelOutlined />
							</IconButton>
						)}
						<Box flex={1} />
						{!isMobile && (
							<EmblemAvatarGroup
								ref={participantsPopupAnchor}
								size={32}
								max={4}
								sx={{ cursor: 'pointer' }}
								onClick={() => setParticipantsPopupOpen(true)}>
								{participants.map((p) => (
									<EmblemAvatar
										key={p.entityId}
										entityId={p.entityId}
										entityType={convertParticipantType(p.entityType)}
										noDropdown
										showBadge={false}
									/>
								))}
							</EmblemAvatarGroup>
						)}
						<IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
							<MoreHoriz />
						</IconButton>
					</Stack>
					{activeLabels.length > 0 && (
						<>
							<Divider />
							<Stack
								direction="row"
								px={2}
								py={1.5}
								gap={1}
								overflow="auto"
								flexWrap={{ xs: 'nowrap', sm: 'wrap' }}>
								{activeLabels.map((label) => (
									<LabelChip key={label.label.id} label={label} />
								))}
							</Stack>
						</>
					)}
				</Paper>
				<Divider />
				<MessagesList messages={conversation.messages} />
				{canEditConversation && (
					<>
						<Divider />
						<Paper elevation={1} sx={{ borderRadius: 0, p: { xs: 1, sm: 1.5 } }}>
							{isMobile ? (
								<Button
									fullWidth
									variant="outlined"
									sx={{
										py: 1,
										backgroundColor: 'neutral.50',
										justifyContent: 'flex-start',
									}}
									onClick={() => setReplyModalOpen(true)}>
									<Typography variant="body2" color="neutral.500">
										Reply to this conversation...
									</Typography>
								</Button>
							) : (
								<Stack minHeight={200} maxHeight={350} overflow="hidden">
									<ConversationMessageBox />
								</Stack>
							)}
						</Paper>
					</>
				)}
			</Stack>
			{/* menu */}
			<Menu
				open={Boolean(menuAnchor)}
				anchorEl={menuAnchor}
				onClose={() => setMenuAnchor(null)}
				onClick={() => setMenuAnchor(null)}>
				{canEditConversation && (
					<MenuItem onClick={() => setEditModalOpen(true)}>
						<ListItemIcon>
							<EditOutlined />
						</ListItemIcon>
						<ListItemText primary="Edit Name" />
					</MenuItem>
				)}
				<MenuItem onClick={() => setParticipantsPopupOpen(true)}>
					<ListItemIcon>
						<PeopleAltOutlined />
					</ListItemIcon>
					<ListItemText
						primary={!canEditConversation ? 'View Participants' : 'Manage Participants'}
					/>
				</MenuItem>
				{!readOnly && !isInTrash && (
					<MenuItem onClick={() => setTrashModalOpen(true)}>
						<ListItemIcon>
							<DeleteOutlined />
						</ListItemIcon>
						<ListItemText primary="Trash Conversation" />
					</MenuItem>
				)}
				{isInTrash && (
					<MenuItem onClick={restoreConversation}>
						<ListItemIcon>
							<RestoreFromTrashOutlined />
						</ListItemIcon>
						<ListItemText primary="Restore Conversation" />
					</MenuItem>
				)}
			</Menu>
			{/* labels popup */}
			<PopoverOrDrawer
				open={labelsPopupOpen}
				onClose={() => setLabelsPopupOpen(false)}
				title="Manage Labels"
				popoverProps={{
					anchorEl: labelsPopupAnchor.current,
					anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
					transformOrigin: { vertical: 'top', horizontal: 'center' },
				}}>
				<LabelsPopup labels={labels} activeLabelIds={activeLabelIds} />
			</PopoverOrDrawer>
			{/* participants popup */}
			<PopoverOrDrawer
				open={participantsPopupOpen}
				onClose={() => setParticipantsPopupOpen(false)}
				title={!canEditConversation ? 'Conversation Participants' : 'Manage Participants'}
				popoverProps={{
					anchorEl: participantsPopupAnchor.current,
					anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
					transformOrigin: { vertical: 'top', horizontal: 'center' },
				}}>
				<ParticipantsPopup
					participants={participants}
					onClose={() => setParticipantsPopupOpen(false)}
					onAdd={() => setAddParticipantsModalOpen(true)}
					onRemove={(p) => {
						setParticipantToRemove(p);
						setRemoveParticipantModalOpen(true);
					}}
					canEdit={canEditConversation}
				/>
			</PopoverOrDrawer>
			{/** add participants modal */}
			<ModalOrDrawer open={addParticipantsModalOpen}>
				<AddParticipantsModal
					existingParticipants={participants}
					onClose={() => setAddParticipantsModalOpen(false)}
				/>
			</ModalOrDrawer>
			{/** remove participant modal */}
			{participantToRemove && (
				<ModalOrDrawer open={removeParticipantModalOpen}>
					<RemoveParticipantModal
						participant={participantToRemove}
						onClose={() => setRemoveParticipantModalOpen(false)}
					/>
				</ModalOrDrawer>
			)}
			{/* reply modal (for mobile) */}
			{isMobile && (
				<Dialog open={replyModalOpen} fullScreen>
					<Stack height="100%">
						<Stack
							px={1.5}
							py={1}
							direction="row"
							alignItems="center"
							justifyContent="space-between"
							spacing={1}>
							<Typography variant="h4">Re: {conversation.subject}</Typography>
							<IconButton onClick={() => setReplyModalOpen(false)}>
								<Close />
							</IconButton>
						</Stack>
						<Divider />
						<Stack px={1.5} py={1} flex={1} overflow="hidden">
							<ConversationMessageBox onSuccess={() => setReplyModalOpen(false)} />
						</Stack>
					</Stack>
				</Dialog>
			)}
			{/** edit conversation modal */}
			<ModalOrDrawer open={editModalOpen}>
				<UpdateConversationModal
					conversation={conversation}
					onClose={() => setEditModalOpen(false)}
				/>
			</ModalOrDrawer>
			{/** trash modal */}
			<ModalOrDrawer open={trashModalOpen}>
				<TrashConversationModal
					conversationName={conversation.subject}
					onClose={() => setTrashModalOpen(false)}
				/>
			</ModalOrDrawer>
		</>
	);
};

/**
 * wrappers for this component
 */
export const ConversationColumn = () => {
	const { selectedConversationId } = useCommunicationsContext();
	if (selectedConversationId !== null)
		return <ConversationColumnContentWrapper conversationId={selectedConversationId} />;
	return <EmptyConversationColumn />;
};

const ConversationColumnContentWrapper = ({ conversationId }: { conversationId: string }) => {
	const { conversation, loading } = useConversation(conversationId);
	if (loading) return <ConversationColumnSkeleton />;
	if (!conversation) return <EmptyConversationColumn />;
	return <_ConversationColumn key={conversation.id} conversation={conversation} />;
};
